import { useState } from 'react';
import { Alert, App, Button, Card, Col, Empty, Flex, Modal, Row, Space, Spin, theme, Tooltip, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faRedo, faSquareXmark } from '@fortawesome/free-solid-svg-icons';
import InsuranceCarrierLogo from 'Components/commonComponents/InsuranceCarrierLogo';
import { useDispatch } from 'react-redux';
import { initializeExpectedPackagesResponsesAction, setPetitionAliveAction } from 'Redux/actions/comparationQuotationActions';
import { addInactivePackages, generateInsuranceQuotation, removePackagesByInsuranceCarriers } from 'Redux/actions/globalQuotation';
import { getQuotationPrice } from 'Pages/DataGatewayPage/routerForm/utils';
import { useLoading } from '@shared/hooks/useLoading';
import { useGetInsuranceCarriers } from '@features/insuranceCarriers/hooks/useInsuranceCarriers';
import { ConnectionTypeTag } from '@/shared/components/ConnectionTypeTag';

const { Paragraph, Text } = Typography;

const TOOLTIP_DELAY = 0.5;

/**
 * Indica si la fila de tipo de conexión permite cotizar (API / RPA + marca).
 * Usa `quotable` del backend cuando existe; si no, misma regla que antes (brand + global en true).
 */
const connectionRowIsQuotable = (connectionRow) => {
  if (typeof connectionRow.quotable === 'boolean') {
    return connectionRow.quotable;
  }
  const brandEnabled = connectionRow.brand_enabled === true || connectionRow.brandEnabled === true;
  const globalEnabled = connectionRow.global_enabled === true || connectionRow.globalEnabled === true;
  return brandEnabled && globalEnabled;
};

/**
 * Filas de conexión con marca habilitada para el modal de recotizar (visualización de tags).
 * Los `code` llegan ya en el formato esperado; `connection_types` o `connectionTypes` según el origen.
 */
const getRecoteConnectionRows = (insuranceCarrier) => {
  const connectionList = insuranceCarrier.connection_types ?? insuranceCarrier.connectionTypes ?? [];
  return connectionList
    .filter((connectionRow) => connectionRow.brand_enabled === true || connectionRow.brandEnabled === true)
    .map((connectionRow) => ({
      code: connectionRow.code,
      name: connectionRow.name ?? connectionRow.code,
      globalEnabled: connectionRow.global_enabled === true || connectionRow.globalEnabled === true,
      quotable: connectionRowIsQuotable(connectionRow),
    }));
};

const buildRequoteErrorDescription = (error) => {
  const responseData = error.response?.data;
  const primaryMessage = responseData?.message ?? error.message ?? 'Ha ocurrido un error inesperado. Por favor intente de nuevo.';
  const rejectedList = responseData?.rejected_insurance_carrier_ids ?? responseData?.response_data?.rejected_insurance_carrier_ids;
  if (Array.isArray(rejectedList) && rejectedList.length > 0) {
    const detail = rejectedList
      .map((entry) => `${entry.id}${entry.reason ? ` (${entry.reason})` : ''}`)
      .join(', ');
    return `${primaryMessage} Detalle: ${detail}`;
  }
  return primaryMessage;
};

export const RequoteActionButton = ({ quotation, packages, tooltip, disabled }) => {
  const [open, setOpen] = useState(false);
  const [selectedInsuranceCarrierIds, setSelectedInsuranceCarrierIds] = useState(new Set());
  const { token: themeToken } = theme.useToken();
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const requoteLoading = useLoading();

  const insuranceCarriersQuery = useGetInsuranceCarriers(undefined, { enabled: open, staleTime: Infinity });

  const loadedInsuranceCarriers = insuranceCarriersQuery.data ?? [];
  const insuranceCarriersVisibleInModal = loadedInsuranceCarriers.filter(
    (insuranceCarrier) => getRecoteConnectionRows(insuranceCarrier).length > 0,
  );
  const insuranceCarriersSelectableForRequote = insuranceCarriersVisibleInModal.filter((insuranceCarrier) =>
    getRecoteConnectionRows(insuranceCarrier).some((connectionRow) => connectionRow.quotable),
  );
  const selectableInsuranceCarrierIds = insuranceCarriersSelectableForRequote.map(({ id }) => id);

  const allowedVisibleInsuranceCarrierIds = new Set(insuranceCarriersVisibleInModal.map((c) => c.id));
  const effectiveSelectedInsuranceCarrierIds = new Set(
    [...selectedInsuranceCarrierIds].filter((id) => allowedVisibleInsuranceCarrierIds.has(id)),
  );

  const isSelectionEmpty = effectiveSelectedInsuranceCarrierIds.size === 0;
  const areAllSelectableChosen =
    selectableInsuranceCarrierIds.length > 0
    && selectableInsuranceCarrierIds.every((id) => effectiveSelectedInsuranceCarrierIds.has(id));

  const handleOpenModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setSelectedInsuranceCarrierIds(new Set());
    setOpen(false);
  };

  const handleRequote = async () => {
    requoteLoading.start();
    try {
      const price = await getQuotationPrice(true);
      const { insurance_carriers: requotedInsuranceCarriers } = await dispatch(
        generateInsuranceQuotation(quotation.id, price, true, Array.from(effectiveSelectedInsuranceCarrierIds)),
      );

      const insuranceCarrierIdsToRequote = requotedInsuranceCarriers.map(({ id }) => id);

      const packagesToRequote = packages.filter((item) => insuranceCarrierIdsToRequote.includes(item.insurance_carrier));
      dispatch(addInactivePackages(packagesToRequote));

      dispatch(removePackagesByInsuranceCarriers(insuranceCarrierIdsToRequote));
      dispatch(initializeExpectedPackagesResponsesAction(requotedInsuranceCarriers));
      dispatch(setPetitionAliveAction(true));

      closeModal();
    } catch (error) {
      notification.error({
        message: 'No se pudo recotizar',
        description: buildRequoteErrorDescription(error),
      });
    } finally {
      requoteLoading.stop();
    }
  };

  const handleCancel = () => {
    closeModal();
  };

  const handleSelectAll = () => {
    setSelectedInsuranceCarrierIds(new Set(selectableInsuranceCarrierIds));
  };

  const handleDeselectAll = () => {
    setSelectedInsuranceCarrierIds(new Set());
  };

  const handleToggleSelect = (insuranceCarrier) => {
    if (!getRecoteConnectionRows(insuranceCarrier).some((connectionRow) => connectionRow.quotable)) return;
    const { id } = insuranceCarrier;
    setSelectedInsuranceCarrierIds((previousSelection) => {
      const nextSelection = new Set(previousSelection);
      nextSelection.has(id) ? nextSelection.delete(id) : nextSelection.add(id);
      return nextSelection;
    });
  };

  return (<>
    <Tooltip title={tooltip}>
      <>
        <Button type='primary' icon={<FontAwesomeIcon icon={faRedo} />} onClick={handleOpenModal} disabled={disabled}>
          Recotizar
        </Button>
      </>
    </Tooltip>
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={faRedo} color={themeToken.colorPrimary} />
          Recotizar aseguradoras
        </Space>
      }
      open={open}
      onOk={handleRequote}
      onCancel={handleCancel}
      okText='Recotizar'
      closable={false}
      maskClosable={false}
      width='40em'
      okButtonProps={{ disabled: isSelectionEmpty }}
      confirmLoading={requoteLoading.isLoading}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Space size='middle'>
          <CancelBtn />
          <Tooltip title={isSelectionEmpty ? 'Seleccione al menos una aseguradora' : null} mouseEnterDelay={TOOLTIP_DELAY}>
            <><OkBtn /></>
          </Tooltip>
        </Space>
      )}
      styles={{ footer: { marginTop: '1em' } }}
    >
      <Paragraph>
        Seleccione las aseguradoras que desea recotizar:
      </Paragraph>
      {insuranceCarriersQuery.isError && (
        <Alert
          type='error'
          showIcon
          message='No se pudieron cargar las aseguradoras'
          description={insuranceCarriersQuery.error?.message ?? 'Intente cerrar el modal y abrirlo de nuevo.'}
          style={{ marginBottom: 16 }}
        />
      )}
      <Spin spinning={Boolean(open && insuranceCarriersQuery.isFetching)}>
        {!insuranceCarriersQuery.isFetching && !insuranceCarriersQuery.isError && insuranceCarriersVisibleInModal.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='No hay aseguradoras con convenio, API o RPA habilitados para su marca.'
          />
        )}
        {!insuranceCarriersQuery.isError && insuranceCarriersVisibleInModal.length > 0 && (
          <Row gutter={[16, 16]} justify='space-evenly'>
            <Col span={24}>
              <Flex justify='space-evenly'>
                <Tooltip title={areAllSelectableChosen ? 'Ya están todas seleccionadas' : null} mouseEnterDelay={TOOLTIP_DELAY}>
                  <Button
                    color='primary'
                    variant='text'
                    icon={<FontAwesomeIcon icon={faCheckSquare} />}
                    onClick={handleSelectAll}
                    disabled={selectableInsuranceCarrierIds.length === 0 || areAllSelectableChosen}
                  >
                    Seleccionar todas
                  </Button>
                </Tooltip>
                <Tooltip title={isSelectionEmpty ? 'No hay aseguradoras seleccionadas' : null} mouseEnterDelay={TOOLTIP_DELAY}>
                  <Button
                    color='danger'
                    variant='text'
                    icon={<FontAwesomeIcon icon={faSquareXmark} />}
                    onClick={handleDeselectAll}
                    disabled={isSelectionEmpty}
                  >
                    Deseleccionar todas
                  </Button>
                </Tooltip>
              </Flex>
            </Col>
            {insuranceCarriersVisibleInModal.map((insuranceCarrier) => {
              const { id, name, logoUrl } = insuranceCarrier;
              const connectionRowsForDisplay = getRecoteConnectionRows(insuranceCarrier);
              const isInsuranceCarrierSelectable = connectionRowsForDisplay.some((connectionRow) => connectionRow.quotable);
              const isSelected = effectiveSelectedInsuranceCarrierIds.has(id);
              return (
                <Col key={id} flex='0 0 10em'>
                  <Card
                    hoverable={isInsuranceCarrierSelectable}
                    size='small'
                    onClick={() => handleToggleSelect(insuranceCarrier)}
                    style={{
                      height: '100%',
                      userSelect: 'none',
                      cursor: isInsuranceCarrierSelectable ? 'pointer' : 'not-allowed',
                      opacity: isInsuranceCarrierSelectable ? 1 : 0.85,
                      ...(isSelected && {
                        backgroundColor: `${themeToken.colorPrimary}0d`,
                        borderColor: `${themeToken.colorPrimary}80`,
                      }),
                    }}
                    styles={{
                      body: {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      },
                    }}
                  >
                    <InsuranceCarrierLogo name={name} logoUrl={logoUrl} size='lg' />
                    <Space size={4} wrap style={{ justifyContent: 'center' }}>
                      {connectionRowsForDisplay.map((connectionRow) => (
                        <ConnectionTypeTag
                          key={connectionRow.code}
                          compact={false}
                          connectionType={connectionRow.code}
                          globalEnabled={connectionRow.globalEnabled}
                          aria-label={connectionRow.name}
                        />
                      ))}
                    </Space>
                    {!isInsuranceCarrierSelectable && (
                      <Text type='secondary' style={{ fontSize: '0.7em', textAlign: 'center' }}>
                        Sin conexión disponible
                      </Text>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Spin>
    </Modal>
  </>);
};
